/**
 * Tarih utility fonksiyonlari
 *
 * Backend'den bazen timezone suffix'i olmadan UTC tarih gelebiliyor.
 * Bu durumda tarihi UTC kabul edip parse ediyoruz.
 */

const hasExplicitTimezone = (value: string): boolean => {
  return /(z|[+-]\d{2}:\d{2})$/i.test(value);
};

const parseBackendDate = (value: string): Date | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  let normalized = trimmed;

  // Timestamp (epoch ms) string destegi
  if (/^\d+$/.test(trimmed)) {
    const epochMs = Number(trimmed);
    const dateFromEpoch = new Date(epochMs);
    return Number.isNaN(dateFromEpoch.getTime()) ? null : dateFromEpoch;
  }

  // Timezone bilgisi yoksa UTC kabul et
  if (!hasExplicitTimezone(trimmed) && trimmed.includes("T")) {
    normalized = `${trimmed}Z`;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Geriye donuk uyumluluk icin korunuyor.
 * Donen Date UTC bazli instant'tir; formatlama asamasinda Europe/Istanbul kullanilir.
 */
export const convertUTCToTurkey = (utcDateString: string): Date => {
  const parsed = parseBackendDate(utcDateString);
  return parsed ?? new Date(utcDateString);
};

/**
 * Tarih string'ini Turkiye saatine gore formatlar.
 */
export const formatDateToTurkeyTime = (
  dateString: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const parsed = parseBackendDate(dateString);
  if (!parsed) return dateString;

  const formatter = new Intl.DateTimeFormat("tr-TR", {
    day: options?.day ?? "numeric",
    month: options?.month ?? "long",
    year: options?.year ?? "numeric",
    hour: options?.hour ?? "2-digit",
    minute: options?.minute ?? "2-digit",
    hour12: false,
    timeZone: "Europe/Istanbul",
  });

  return formatter.format(parsed);
};

/**
 * Kisa tarih formati (OrdersManagement)
 * Ornek: "15 Ara 2024 14:30"
 */
export const formatShortDate = (dateString: string): string => {
  return formatDateToTurkeyTime(dateString, {
    month: "short",
  });
};

/**
 * Uzun tarih formati (OrderDetailsModal)
 * Ornek: "15 Aralik 2024 14:30"
 */
export const formatLongDate = (dateString: string): string => {
  return formatDateToTurkeyTime(dateString, {
    month: "long",
  });
};

/**
 * Gun/ay/yil formati (ReviewsManagement)
 */
export const formatReviewDate = (dateString: string): string => {
  return formatDateToTurkeyTime(dateString, {
    day: "2-digit",
    month: "short",
  });
};

/**
 * Sadece tarih (saat yok)
 */
export const formatDateOnly = (dateString: string): string => {
  const parsed = parseBackendDate(dateString);
  if (!parsed) return dateString;

  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Istanbul",
  }).format(parsed);
};

